import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { User, Mail, Phone, Loader2, Save } from 'lucide-react';
import { toast } from 'sonner';

export default function CustomerProfile() {
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState({ name: '', phone: '', email: '' });
  const [pwForm, setPwForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [changingPw, setChangingPw] = useState(false);

  useEffect(() => {
    api.get('/customer/profile')
      .then(r => {
        setProfile(r.data.customer);
        setForm({
          name: r.data.customer?.name || '',
          phone: r.data.customer?.phone || '',
          email: r.data.customer?.email || '',
        });
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Nama tidak boleh kosong'); return; }
    setSaving(true);
    try {
      await api.put('/customer/profile', { name: form.name, phone: form.phone });
      toast.success('Profil berhasil diperbarui');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal menyimpan profil');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (!pwForm.current_password || !pwForm.new_password) { toast.error('Lengkapi semua field'); return; }
    if (pwForm.new_password !== pwForm.confirm_password) { toast.error('Konfirmasi password tidak cocok'); return; }
    if (pwForm.new_password.length < 6) { toast.error('Password minimal 6 karakter'); return; }
    setChangingPw(true);
    try {
      await api.put('/customer/profile/password', {
        current_password: pwForm.current_password,
        new_password: pwForm.new_password
      });
      toast.success('Password berhasil diubah');
      setPwForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal mengubah password');
    } finally {
      setChangingPw(false);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--store-primary, #2D6CDF)]" />
    </div>
  );

  return (
    <div data-testid="customer-profile-page">
      <div className="mb-6">
        <h1 className="text-2xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>Profil Saya</h1>
        <p className="text-sm text-slate-500 mt-1">Kelola informasi akun Anda</p>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Profile Info */}
        <Card className="border-slate-200 shadow-sm">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <User size={18} className="text-[var(--store-primary, #2D6CDF)]" /> Informasi Pribadi
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Avatar */}
            <div className="flex items-center gap-4 mb-2">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[var(--store-primary, #2D6CDF)] to-blue-400 flex items-center justify-center text-white text-2xl font-bold">
                {form.name?.[0]?.toUpperCase() || 'C'}
              </div>
              <div>
                <p className="font-semibold text-slate-900">{form.name}</p>
                <p className="text-sm text-slate-500">{form.email}</p>
              </div>
            </div>

            <div>
              <Label htmlFor="profile-name">Nama Lengkap</Label>
              <Input
                id="profile-name"
                value={form.name}
                onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="profile-email">Email</Label>
              <Input
                id="profile-email"
                value={form.email}
                disabled
                className="mt-1 bg-slate-50"
              />
              <p className="text-xs text-slate-400 mt-1">Email tidak dapat diubah</p>
            </div>
            <div>
              <Label htmlFor="profile-phone">Nomor HP</Label>
              <Input
                id="profile-phone"
                value={form.phone}
                onChange={e => setForm(p => ({ ...p, phone: e.target.value }))}
                placeholder="08xxxxxxxxxx"
                className="mt-1"
              />
            </div>
            <Button onClick={handleSave} disabled={saving} className="w-full bg-[var(--store-primary, #2D6CDF)] hover:bg-[var(--store-primary-hover, #1A5BC4)]">
              {saving ? <><Loader2 size={16} className="mr-2 animate-spin" /> Menyimpan...</> : <><Save size={16} className="mr-2" /> Simpan Perubahan</>}
            </Button>
          </CardContent>
        </Card>

        {/* Change Password */}
        <Card className="border-slate-200 shadow-sm">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Mail size={18} className="text-violet-500" /> Ubah Password
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="curr-pw">Password Saat Ini</Label>
              <Input
                id="curr-pw"
                type="password"
                value={pwForm.current_password}
                onChange={e => setPwForm(p => ({ ...p, current_password: e.target.value }))}
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="new-pw">Password Baru</Label>
              <Input
                id="new-pw"
                type="password"
                value={pwForm.new_password}
                onChange={e => setPwForm(p => ({ ...p, new_password: e.target.value }))}
                placeholder="Minimal 6 karakter"
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="conf-pw">Konfirmasi Password Baru</Label>
              <Input
                id="conf-pw"
                type="password"
                value={pwForm.confirm_password}
                onChange={e => setPwForm(p => ({ ...p, confirm_password: e.target.value }))}
                className="mt-1"
              />
            </div>
            <Button onClick={handleChangePassword} disabled={changingPw} className="w-full bg-violet-600 hover:bg-violet-700">
              {changingPw ? <><Loader2 size={16} className="mr-2 animate-spin" /> Mengubah...</> : 'Ubah Password'}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
